import { useState, useEffect, useRef, useCallback } from "react";
import type { VideoJob } from "../lib/types";
import { listVideoJobs, getVideoJob } from "../lib/api";

const POLL_INTERVAL_MS = 3000;

export function useVideoJobs(parentJobId: string | null) {
  const [videos, setVideos] = useState<VideoJob[]>([]);
  const [loaded, setLoaded] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videosRef = useRef<VideoJob[]>([]);

  // Keep ref in sync with state for use inside interval callbacks
  useEffect(() => {
    videosRef.current = videos;
  }, [videos]);

  const isAnyActive = videos.some(
    (v) => v.status === "queued" || v.status === "processing"
  );

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshActiveJobs = useCallback(async () => {
    if (!parentJobId) return;

    const active = videosRef.current.filter(
      (v) => v.status === "queued" || v.status === "processing"
    );
    if (active.length === 0) {
      stopPolling();
      return;
    }

    const updated = await Promise.all(
      active.map((v) => getVideoJob(parentJobId, v.videoJobId).catch(() => null))
    );

    setVideos((current) =>
      current.map((v) => {
        const fresh = updated.find((u) => u?.videoJobId === v.videoJobId);
        return fresh ?? v;
      })
    );
  }, [parentJobId, stopPolling]);

  // Load video jobs when parentJobId becomes available
  useEffect(() => {
    if (!parentJobId) return;

    setLoaded(false);
    setVideos([]);

    listVideoJobs(parentJobId)
      .then((jobs) => {
        setVideos(jobs);
        setLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load video jobs:", err);
        setLoaded(true);
      });
  }, [parentJobId]);

  // Start/stop polling based on active jobs
  useEffect(() => {
    if (!loaded || !parentJobId) return;

    if (isAnyActive && !pollRef.current) {
      pollRef.current = setInterval(refreshActiveJobs, POLL_INTERVAL_MS);
    } else if (!isAnyActive) {
      stopPolling();
    }

    return stopPolling;
  }, [isAnyActive, loaded, parentJobId, refreshActiveJobs, stopPolling]);

  const addVideoJob = useCallback((job: VideoJob) => {
    setVideos((prev) => [job, ...prev]);
  }, []);

  const removeVideoJob = useCallback((videoJobId: string) => {
    setVideos((prev) => prev.filter((v) => v.videoJobId !== videoJobId));
  }, []);

  return { videos, isAnyActive, loaded, addVideoJob, removeVideoJob };
}
